import type { EmployeeProfileResponseDto } from "@/generated/model/employeeProfileResponseDto";
import {
  accountStatusLabel,
  emergencyContactName,
  formatProfileTimestamp,
  lastUpdatedLabel,
  primaryContactEmail,
  textOrNotRecorded,
} from "@/lib/people";
import { staffUsername } from "@/lib/staff-username";

import { ProfileSectionCard } from "../ProfileSectionCard";
import { AdditionalNotesSection } from "../sections/AdditionalNotesSection";
import { AllergyList } from "../sections/AllergiesSection";
import { WithheldSectionCard } from "../SensitiveSectionNotes";

interface ProfileOverviewTabProps {
  nurseryId: string;
  userId: string;
  profile: EmployeeProfileResponseDto;
}

export function ProfileOverviewTab({
  nurseryId,
  userId,
  profile,
}: ProfileOverviewTabProps) {
  const {
    keyInformation,
    contactDetails,
    emergencyContact,
    allergies,
    loginAndSecurity,
  } = profile;

  return (
    <div className="flex w-full flex-col gap-6">
      <div className="grid w-full grid-cols-1 gap-6 lg:grid-cols-2">
        <ProfileSectionCard
          title="Employee snapshot"
          testId="profile-summary-snapshot"
          fields={[
            {
              label: "Employee ID",
              value: textOrNotRecorded(keyInformation.employeeId),
            },
            { label: "Username", value: staffUsername(keyInformation.email) },
            {
              label: "Preferred name",
              value: textOrNotRecorded(keyInformation.preferredName),
            },
            {
              label: "Pronouns",
              value: textOrNotRecorded(keyInformation.pronouns),
            },
          ]}
        />
        <ProfileSectionCard
          title="Primary contact"
          testId="profile-summary-primary-contact"
          fields={[
            {
              label: "Mobile",
              value: textOrNotRecorded(contactDetails.mobile),
            },
            { label: "Email", value: primaryContactEmail(contactDetails) },
          ]}
        />
        <ProfileSectionCard
          title="Emergency contact"
          testId="profile-summary-emergency-contact"
          fields={[
            {
              label: "Name",
              value: emergencyContactName(
                emergencyContact.name,
                emergencyContact.relationship,
              ),
            },
            {
              label: "Mobile",
              value: textOrNotRecorded(emergencyContact.mobile),
            },
          ]}
        />
        {allergies ? (
          <ProfileSectionCard
            title="Allergies"
            testId="profile-summary-allergies"
          >
            <AllergyList allergies={allergies} />
          </ProfileSectionCard>
        ) : (
          <WithheldSectionCard
            title="Allergies"
            testId="profile-summary-allergies-withheld"
          />
        )}
      </div>
      <AdditionalNotesSection
        nurseryId={nurseryId}
        userId={userId}
        additionalNotes={profile.additionalNotes}
        canEdit={profile.capabilities.canEdit}
      />
      {loginAndSecurity && (
        <ProfileSectionCard
          title="Record status"
          testId="profile-record-status"
          fields={[
            {
              label: "Account status",
              value: accountStatusLabel(loginAndSecurity.accountStatus),
            },
            {
              label: "Last login",
              value: formatProfileTimestamp(loginAndSecurity.lastLoginAt),
            },
            {
              label: "Account created",
              value: formatProfileTimestamp(loginAndSecurity.accountCreatedAt),
            },
            {
              label: "Last updated",
              value: lastUpdatedLabel(
                profile.lastUpdatedAt,
                profile.lastUpdatedBy,
              ),
            },
          ]}
        />
      )}
    </div>
  );
}
